/**
 * GEN-04-23 (Angela, 21 Oct 2026): on every list page the filter box under the
 * title and the table's header row stay at the top while the page scrolls,
 * under the path and title (GEN-04-21).
 *
 * Pages build their own toolbars and tables, so the layout finds them:
 * - the filter box is the nearest block before the page's first table that
 *   holds a search box, a select or a button (and no table of its own);
 * - a table's header row sticks when the table scrolls with the page — a table
 *   inside a box that scrolls by itself (a wide table, a drawer, a popup)
 *   keeps its header where it is.
 *
 * A page opts out with `data-no-sticky-list` on any ancestor of the table, and
 * marks a header that is not a table row (a calendar's weekdays) with
 * `data-sticky-row`. A table or calendar wider than the page scrolls in its own
 * box, sized to the window, with its header stuck at the top of that box.
 */

const FILTER_ATTR = 'data-sticky-filter';
const HEAD_ATTR = 'data-sticky-head';
const UNWRAP_ATTR = 'data-sticky-unwrapped';
/** On a box that scrolls by itself: 'cap' when the layout gave it its height. */
const BOX_ATTR = 'data-sticky-box';
/** A header that sticks inside such a box. */
const INBOX_ATTR = 'data-sticky-inbox';
/** Set by a page on a non-table header row (a calendar's weekdays). */
const ROW_SELECTOR = '[data-sticky-row]';
const MAX_FILTER_HEIGHT = 180;

const INTERACTIVE = 'input, select, textarea, button, [role="combobox"], [role="searchbox"]';

function isOwnSticky(el: Element): boolean {
  return getComputedStyle(el).position === 'sticky';
}

function isFilterRow(el: HTMLElement): boolean {
  // A toolbar the page already made sticky counts too: it is moved down to sit
  // under the path and title instead of sliding behind them.
  return (
    !el.matches('[data-testid="page-heading"]') &&
    !el.querySelector('table') &&
    el.offsetHeight > 0 &&
    el.offsetHeight <= MAX_FILTER_HEIGHT &&
    (el.matches(INTERACTIVE) || el.querySelector(INTERACTIVE) !== null)
  );
}

/**
 * The filter box for `table`: the earlier sibling rows right before it (a page
 * may split its filters over two rows), found by walking up to `root`. Returned
 * top to bottom.
 */
function findFilterRows(root: HTMLElement, table: HTMLTableElement): HTMLElement[] {
  for (let node: HTMLElement | null = table; node && node !== root; node = node.parentElement) {
    const rows: HTMLElement[] = [];
    let sib = node.previousElementSibling as HTMLElement | null;
    while (sib && sib.offsetHeight === 0) sib = sib.previousElementSibling as HTMLElement | null;
    while (sib && rows.length < 3 && isFilterRow(sib)) {
      rows.unshift(sib);
      sib = sib.previousElementSibling as HTMLElement | null;
    }
    if (rows.length > 0) return rows;
    if (sib) return []; // something else sits right before it: no filter box
  }
  return [];
}

/**
 * A GEN-04-14 toolbar box: a light grey (or ground) background — not white,
 * not see-through. Colours arrive as rgb() or, from Tailwind v4, oklch().
 */
function isLightGrey(bg: string): boolean {
  const rgb = bg.match(/^rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/);
  if (rgb) {
    const [r, g, b] = rgb.slice(1, 4).map(Number);
    const light = Math.min(r, g, b) >= 225;
    return light && !(r === 255 && g === 255 && b === 255);
  }
  const ok = bg.match(/^oklch\(\s*([\d.]+)(%?)/);
  if (ok) {
    const l = parseFloat(ok[1]) / (ok[2] === '%' ? 100 : 1);
    return l >= 0.9 && l < 0.999;
  }
  return false;
}

/**
 * Angela, 22 Oct 2026: the grey toolbar box sticks on EVERY page, also when
 * cards or charts sit between it and the first table (reports) or there is no
 * table at all. Fallback when no filter box sits right before the first table:
 * the first grey box with controls near the top of the page.
 */
function findTopToolbar(root: HTMLElement): HTMLElement[] {
  const rootTop = root.getBoundingClientRect().top;
  const candidates: HTMLElement[] = [];
  const walk = (el: HTMLElement, depth: number) => {
    for (const child of Array.from(el.children) as HTMLElement[]) {
      if (child.offsetHeight === 0 || child.matches('[data-testid="page-heading"]')) continue;
      candidates.push(child);
      if (depth < 3) walk(child, depth + 1);
    }
  };
  walk(root, 0);
  for (const el of candidates) {
    if (el.getBoundingClientRect().top - rootTop > 320) continue;
    if (el.closest('[data-no-sticky-list], [role="dialog"], .gen08-window')) continue;
    const bg = ownBackground(el);
    if (bg && isLightGrey(bg) && isFilterRow(el)) return [el];
  }
  return [];
}

/** Height of the page's own sticky rows (e.g. a tab row) directly in `root`'s top blocks. */
function ownStickyHeight(root: HTMLElement, filterRows: HTMLElement[]): number {
  let h = 0;
  root.querySelectorAll<HTMLElement>(':scope > *, :scope > * > *').forEach((el) => {
    if (el.matches('[data-testid="page-heading"]') || filterRows.includes(el)) return;
    if (isOwnSticky(el) && el.offsetHeight > 0) h = Math.max(h, el.offsetHeight);
  });
  return h;
}

function ownBackground(el: Element): string | null {
  const bg = getComputedStyle(el).backgroundColor;
  return bg && bg !== 'transparent' && !/rgba\(\s*0,\s*0,\s*0,\s*0\s*\)/.test(bg) ? bg : null;
}

/** The first solid background among `els` (header row, thead, table). */
function firstBackground(...els: (Element | null | undefined)[]): string | null {
  for (const el of els) {
    const bg = el ? ownBackground(el) : null;
    if (bg) return bg;
  }
  return null;
}

type HeadMode =
  | { kind: 'page' }
  | { kind: 'box'; box: HTMLElement; capped: boolean }
  | { kind: 'none' };

/**
 * Where a header (a table's header row, or a row marked `data-sticky-row` such
 * as a calendar's weekdays) can stick:
 * - 'page' — no box in between scrolls: it sticks under the title and filters;
 * - 'box' — it sits in a box that scrolls sideways (a table wider than the page)
 *   or has its own height: that box gets a height that fits the window and the
 *   header sticks at the top of the box;
 * - 'none' — inside a popup or side window, or opted out.
 */
function headMode(root: HTMLElement, target: HTMLElement): HeadMode {
  if (target.closest('[data-no-sticky-list], [role="dialog"], .gen08-window')) return { kind: 'none' };
  const boxes: HTMLElement[] = [];
  for (let el = target.parentElement; el && el !== root; el = el.parentElement) {
    const cs = getComputedStyle(el);
    if (cs.position === 'fixed') return { kind: 'none' };
    if (el.hasAttribute(BOX_ATTR)) return { kind: 'box', box: el, capped: el.getAttribute(BOX_ATTR) === 'cap' };
    if (cs.overflowX === 'visible' && cs.overflowY === 'visible') continue;
    if (cs.maxHeight !== 'none' || el.scrollHeight > el.clientHeight + 1) {
      return { kind: 'box', box: el, capped: false };
    }
    if (el.scrollWidth > el.clientWidth + 1) return { kind: 'box', box: el, capped: true };
    boxes.push(el);
  }
  boxes.forEach((el) => el.setAttribute(UNWRAP_ATTR, ''));
  return { kind: 'page' };
}

/** Marks the filter box and the sticky tables under `root`; publishes the offsets on `main`. */
export function applyStickyList(main: HTMLElement, root: HTMLElement): void {
  const tables = Array.from(root.querySelectorAll('table'));
  const beforeTable = tables.length > 0 ? findFilterRows(root, tables[0]) : [];
  const rows = beforeTable.length > 0 ? beforeTable : findTopToolbar(root);
  const own = ownStickyHeight(root, rows);
  root.querySelectorAll<HTMLElement>(`[${FILTER_ATTR}]`).forEach((el) => {
    if (!rows.includes(el)) {
      el.removeAttribute(FILTER_ATTR);
      el.style.removeProperty('--sticky-filter-top');
    }
  });
  let filterH = 0;
  rows.forEach((row) => {
    row.setAttribute(FILTER_ATTR, '');
    row.style.setProperty('--sticky-filter-top', `${filterH}px`);
    // A see-through toolbar gets the page colour, or the rows would show through.
    if (!ownBackground(row) && !row.style.backgroundColor) row.style.backgroundColor = 'var(--color-background, #fff)';
    filterH += row.offsetHeight;
  });
  main.style.setProperty('--page-own-sticky-h', `${own}px`);
  main.style.setProperty('--page-filter-h', `${filterH}px`);

  root.querySelectorAll<HTMLElement>(`[${UNWRAP_ATTR}]`).forEach((el) => el.removeAttribute(UNWRAP_ATTR));
  root.querySelectorAll<HTMLElement>(`[${BOX_ATTR}]`).forEach((el) => el.removeAttribute(BOX_ATTR));
  // What a box that scrolls by itself may be tall: the window below the title,
  // filters and the page's own sticky rows (never under 240 px).
  const boxH = Math.max(240, main.clientHeight - (parseFloat(getComputedStyle(main).getPropertyValue('--page-heading-h')) || 0) - own - filterH - 24);
  main.style.setProperty('--page-scrollbox-h', `${boxH}px`);

  const targets: { el: HTMLElement; bgFrom: (Element | null | undefined)[] }[] = [];
  tables.forEach((table) => {
    if (table.tHead) targets.push({ el: table, bgFrom: [table.tHead.rows[0], table.tHead, table] });
    else table.removeAttribute(HEAD_ATTR);
  });
  root.querySelectorAll<HTMLElement>(ROW_SELECTOR).forEach((row) => targets.push({ el: row, bgFrom: [row] }));

  targets.forEach(({ el, bgFrom }) => {
    const mode = headMode(root, el);
    el.toggleAttribute(HEAD_ATTR, mode.kind === 'page');
    el.toggleAttribute(INBOX_ATTR, mode.kind === 'box');
    if (mode.kind === 'box') mode.box.setAttribute(BOX_ATTR, mode.capped ? 'cap' : 'own');
    if (mode.kind !== 'none') el.style.setProperty('--sticky-th-bg', firstBackground(...bgFrom) ?? '#fff');
  });
}

/** Keeps `applyStickyList` current as the page renders, loads and resizes. Returns a cleanup. */
export function watchStickyList(main: HTMLElement, root: HTMLElement): () => void {
  let timer = 0;
  const schedule = () => {
    if (timer) return;
    timer = window.setTimeout(() => {
      timer = 0;
      applyStickyList(main, root);
    }, 30);
  };
  schedule();
  const mutations = new MutationObserver((records) => {
    if (records.length > 0) schedule();
  });
  mutations.observe(root, { childList: true, subtree: true });
  const sizes = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
  sizes?.observe(root);
  window.addEventListener('resize', schedule);
  return () => {
    if (timer) window.clearTimeout(timer);
    mutations.disconnect();
    sizes?.disconnect();
    window.removeEventListener('resize', schedule);
  };
}
